import { useState, useEffect, useCallback } from "react";

// ── Constants ────────────────────────────────────────────────────────────────

const COMPOUNDS = {
  tirzepatide: { label: "Tirzepatide", color: "#4a9eff", unit: "mg", defaultDose: 5,  routes: ["SubQ"] },
  semaglutide: { label: "Semaglutide", color: "#a06af0", unit: "mg", defaultDose: 0.5, routes: ["SubQ"] },
  klow:        { label: "KLOW",        color: "#e8a840", unit: "units", defaultDose: 15, routes: ["SubQ"] },
  nad:         { label: "NAD+",        color: "#7ec8a0", unit: "mg", defaultDose: 25, routes: ["SubQ", "IM", "IV"] },
};

const BIOMARKERS = [
  "PSA", "hsCRP", "IGF-1", "CBC", "CMP", "Lipids",
  "BP Systolic", "BP Diastolic", "Weight (lbs)",
  "NAD+ Intracellular (μM)", "Testosterone", "HbA1c"
];

const INJECT_SITES = [
  "Abdomen L-Upper", "Abdomen L-Lower", "Abdomen R-Upper", "Abdomen R-Lower",
  "Post. Arm L", "Post. Arm R",
  "Thigh L", "Thigh R",
  "Deltoid L", "Deltoid R",
  "Glute L", "Glute R",
];

const PHASES = [
  { week: "1–2",  label: "Tolerance",   klow: "10–15u/day",  nad: "25mg EOD",       tirz: "Maintain" },
  { week: "3–4",  label: "Ramp",        klow: "20–30u/day",  nad: "50mg 3×/wk",     tirz: "Maintain" },
  { week: "5+",   label: "Maintenance", klow: "30–45u/day",  nad: "100mg 2–3×/wk",  tirz: "5–10mg/wk" },
];

// ── Phase dose calculator — derives units from recon settings ────────────────

function calcUnits(reconSettings, compoundKey, mgDose) {
  const s = reconSettings[compoundKey];
  if (!s) return '—';
  const unitsPerMl = s.syringe === 'U100' ? 100 : 40;
  const mlPerUnit  = 1 / unitsPerMl;
  const concTotal  = s.bacWater > 0 ? s.totalMg / s.bacWater : 0;
  if (concTotal === 0) return '—';
  const units = mgDose / (concTotal * mlPerUnit * 0.001); // mg → μg then units
  return Math.round(units) + 'u';
}

// Phase definitions use mg doses — units derived live from recon settings
const PHASE_DEFS = [
  {
    week: '1–2', label: 'Tolerance',
    compounds: [
      { key: 'klow', label: 'KLOW', mgMin: null, mgMax: null, unitMin: 10, unitMax: 15, suffix: '/day', color: '#e8a840' },
      { key: 'nad',  label: 'NAD+', mgMin: 25,   mgMax: 25,   suffix: ' EOD',           color: '#7ec8a0' },
      { key: 'tirzepatide', label: 'Tirz', mgMin: null, mgMax: null, fixed: 'Maintain', color: '#4a9eff' },
      { key: 'semaglutide', label: 'Sema', mgMin: null, mgMax: null, fixed: '—',        color: '#a06af0' },
    ],
  },
  {
    week: '3–4', label: 'Ramp',
    compounds: [
      { key: 'klow', label: 'KLOW', unitMin: 20, unitMax: 30, suffix: '/day',            color: '#e8a840' },
      { key: 'nad',  label: 'NAD+', mgMin: 50,   mgMax: 50,   suffix: ' 3×/wk',         color: '#7ec8a0' },
      { key: 'tirzepatide', label: 'Tirz', mgMin: null, mgMax: null, fixed: 'Maintain', color: '#4a9eff' },
      { key: 'semaglutide', label: 'Sema', mgMin: null, mgMax: null, fixed: '—',        color: '#a06af0' },
    ],
  },
  {
    week: '5+', label: 'Maintenance',
    compounds: [
      { key: 'klow', label: 'KLOW', unitMin: 30, unitMax: 45, suffix: '/day',            color: '#e8a840' },
      { key: 'nad',  label: 'NAD+', mgMin: 100,  mgMax: 100,  suffix: ' 2–3×/wk',       color: '#7ec8a0' },
      { key: 'tirzepatide', label: 'Tirz', mgMin: 5, mgMax: 10, suffix: 'mg/wk',        color: '#4a9eff' },
      { key: 'semaglutide', label: 'Sema', mgMin: 0.5, mgMax: 2.0, suffix: 'mg/wk',    color: '#a06af0' },
    ],
  },
];

function getPhaseDoseLabel(compound, reconSettings) {
  if (compound.fixed !== undefined) return compound.fixed;

  const s = reconSettings[compound.key];
  if (!s || s.bacWater <= 0 || s.totalMg <= 0) return '—';

  // concentration in mg/mL
  const concMgMl   = s.totalMg / s.bacWater;
  const unitsPerMl = s.syringe === 'U100' ? 100 : 40;

  // Convert a mg dose to units: units = (mgDose / concMgMl) * unitsPerMl
  function mgToUnits(mg) {
    return Math.round((mg / concMgMl) * unitsPerMl);
  }

  // KLOW: defined in units (protocol range) — derive mg from units
  if (compound.unitMin !== undefined) {
    const mlPerUnit = 1 / unitsPerMl;
    const mgMin     = concMgMl * compound.unitMin * mlPerUnit;
    const mgMax     = concMgMl * compound.unitMax * mlPerUnit;
    return `${compound.unitMin}–${compound.unitMax}u${compound.suffix} (${mgMin.toFixed(1)}–${mgMax.toFixed(1)}mg)`;
  }

  // All other compounds: defined in mg — derive units
  if (compound.mgMin !== null) {
    const uMin    = mgToUnits(compound.mgMin);
    const uMax    = mgToUnits(compound.mgMax);
    const mgLabel = compound.mgMin === compound.mgMax
      ? `${compound.mgMin}mg`
      : `${compound.mgMin}–${compound.mgMax}mg`;
    const uLabel  = uMin === uMax ? `${uMin}u` : `${uMin}–${uMax}u`;
    return `${mgLabel} · ${uLabel}${compound.suffix}`;
  }

  return '—';
}

const SAFETY_GATES = [
  { marker: "PSA — any unexplained rise", action: "Pause ALL peptides immediately. Consult urologist before resuming. This gate supersedes all others.", level: "critical" },
  { marker: "hsCRP >3.0 mg/L", action: "Rule out infection/injury first. Review tirzepatide dose. Consider pausing KLOW for 2 weeks.", level: "warning" },
  { marker: "IGF-1 elevated above range", action: "Pause KLOW cycle. Re-evaluate in 4 weeks. Note: KLOW does not stimulate IGF-1 axis — rule out other causes.", level: "warning" },
  { marker: "NAD+ intracellular <40μM", action: "Increase injection frequency or add CD38 inhibitor (apigenin 50mg or quercetin 500mg daily).", level: "info" },
  { marker: "Persistent injection site nodules", action: "Rotate sites more aggressively. Reduce KLOW injection volume. Slow injection rate to 60+ seconds.", level: "info" },
];

// ── API helpers ──────────────────────────────────────────────────────────────

const api = {
  get: (url) => fetch(url).then(r => r.json()),
  post: (url, body) => fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).then(r => r.json()),
  del: (url) => fetch(url, { method: 'DELETE' }).then(r => r.json()),
  patch: (url, body) => fetch(url, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).then(r => r.json()),
};

// ── Utilities ────────────────────────────────────────────────────────────────

function today() { return new Date().toISOString().split('T')[0]; }

function fmtDate(s) {
  if (!s) return '—';
  const d = new Date(s + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function weeksSince(dateStr) {
  if (!dateStr) return 0;
  const diff = Date.now() - new Date(dateStr + 'T00:00:00').getTime();
  return Math.max(1, Math.ceil(diff / (7 * 24 * 60 * 60 * 1000)));
}

// ── Shared styles ────────────────────────────────────────────────────────────

const S = {
  page: {
    minHeight: '100vh', background: '#080a0f', color: '#d4cfc7',
    fontFamily: "'Palatino Linotype', 'Book Antiqua', Palatino, serif",
  },
  input: {
    background: '#080a0f', border: '1px solid #1e2230', borderRadius: 4,
    color: '#c8c0b0', padding: '6px 10px', fontSize: '0.78rem',
    fontFamily: 'monospace', width: '100%', boxSizing: 'border-box', outline: 'none',
  },
  btn: (color = '#4a9eff', bg = '#0a1428', border = '#1e3050') => ({
    background: bg, border: `1px solid ${border}`, color,
    borderRadius: 4, padding: '5px 14px', cursor: 'pointer',
    fontSize: '0.75rem', fontFamily: 'monospace', letterSpacing: '0.05em',
  }),
  card: (borderColor = '#13161f', leftAccent = null) => ({
    background: '#0c0f1a',
    border: `1px solid ${borderColor}`,
    ...(leftAccent ? { borderLeft: `3px solid ${leftAccent}` } : {}),
    borderRadius: 5, padding: '0.85rem 1rem',
  }),
  label: { fontSize: '0.65rem', fontFamily: 'monospace', color: '#445', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.3rem' },
  mono: (color = '#888') => ({ fontFamily: 'monospace', color, fontSize: '0.75rem' }),
};

// ── Sub-components ───────────────────────────────────────────────────────────

function SectionHeader({ children }) {
  return (
    <div style={{ fontSize: '0.65rem', fontFamily: 'monospace', color: '#334', letterSpacing: '0.12em', textTransform: 'uppercase', margin: '1.25rem 0 0.75rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
      <div style={{ flex: 1, height: 1, background: '#13161f' }} />
      {children}
      <div style={{ flex: 1, height: 1, background: '#13161f' }} />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div style={S.label}>{label}</div>
      {children}
    </div>
  );
}

function Tag({ children, color = '#445', bg = '#0d0f18', border = '#1e2230' }) {
  return <span style={{ fontSize: '0.62rem', fontFamily: 'monospace', background: bg, color, border: `1px solid ${border}`, padding: '2px 7px', borderRadius: 3 }}>{children}</span>;
}

// ── Universal Reconstitution Calculator ─────────────────────────────────────

const RECON_DEFAULTS = {
  klow: {
    label: 'KLOW', color: '#e8a840',
    type: 'blend',
    vialMg: 80,
    components: [
      { label: 'GHK-Cu',   mg: 50 },
      { label: 'BPC-157',  mg: 10 },
      { label: 'TB-500',   mg: 10 },
      { label: 'KPV',      mg: 10 },
    ],
    bacWater: 3.0, vialMl: 3.0, syringe: 'U100',
    doseRows: [5, 10, 15, 20, 25, 30, 40, 45, 50],
    rangeMin: 10, rangeMax: 45,
    notes: 'Inject bac water slowly down vial wall · swirl gently, do not shake · use within 7–14 days refrigerated · never refreeze',
  },
  nad: {
    label: 'NAD+', color: '#7ec8a0',
    type: 'single',
    vialMg: 500,
    components: [{ label: 'NAD+', mg: 500 }],
    bacWater: 5.0, vialMl: 5.0, syringe: 'U100',
    doseRows: [25, 50, 75, 100, 125, 150, 200],
    rangeMin: 50, rangeMax: 150,
    notes: 'Use bacteriostatic water only · refrigerate after reconstitution · use within 28–30 days · inject slowly over 45–60 sec to reduce site reaction',
  },
  semaglutide: {
    label: 'Semaglutide', color: '#a06af0',
    type: 'single',
    vialMg: 3,
    components: [{ label: 'Semaglutide', mg: 3 }],
    bacWater: 1.5, vialMl: 1.5, syringe: 'U100',
    doseRows: [5, 10, 15, 20, 25, 30, 40],
    rangeMin: 5, rangeMax: 40,
    notes: 'Ozempic/Wegovy: pre-filled pens standard · compounded vials: refrigerate, use within 28 days · 0.25mg = ~12.5u at 2mg/mL · titrate slowly',
    unitNote: 'Doses in mg: 0.25 / 0.5 / 1.0 / 2.0mg weekly',
  },
  tirzepatide: {
    label: 'Tirzepatide', color: '#4a9eff',
    type: 'single',
    vialMg: 40,
    components: [{ label: 'Tirzepatide', mg: 40 }],
    bacWater: 4.0, vialMl: 4.0, syringe: 'U100',
    doseRows: [5, 10, 15, 20, 25, 30, 40, 50, 60],
    rangeMin: 10, rangeMax: 50,
    notes: 'Mounjaro/Zepbound: pre-filled pens standard · compounded vials: refrigerate, use within 28 days · 5mg = ~50u at 10mg/mL · titrate 2.5mg every 4 weeks',
    unitNote: 'Doses in mg: 2.5 / 5 / 7.5 / 10 / 12.5 / 15mg weekly',
  },
  retatrutide: {
    label: 'Retatrutide', color: '#c070e0',
    type: 'single',
    vialMg: 20,
    components: [{ label: 'Retatrutide', mg: 20 }],
    bacWater: 2.0, vialMl: 2.0, syringe: 'U100',
    doseRows: [5, 10, 15, 20, 25, 30, 40, 50],
    rangeMin: 10, rangeMax: 50,
    notes: 'NOT FDA APPROVED — research use only · no approved dosing exists · Phase 3 TRIUMPH trials ongoing · compounded retatrutide is illegal and unregulated · calculator provided for research reference only',
    unitNote: 'Phase 2 doses: 1 / 4 / 8 / 12mg weekly (investigational only)',
    warning: true,
  },
};

function ReconCalc({ compoundKey, onSettingsChange }) {
  const def = RECON_DEFAULTS[compoundKey];
  const [components, setComponents] = useState(def.components.map(c => ({ ...c })));
  const [bacWater, setBacWater]     = useState(def.bacWater);
  const [vialMl, setVialMl]         = useState(def.vialMl);
  const [syringe, setSyringe]       = useState(def.syringe);
  const [showVars, setShowVars]     = useState(false);

  const totalMg      = components.reduce((s, c) => s + (parseFloat(c.mg) || 0), 0);

  // Notify parent whenever settings change
  useEffect(() => {
    if (onSettingsChange) {
      onSettingsChange(compoundKey, { totalMg, bacWater, vialMl, syringe });
    }
  }, [totalMg, bacWater, vialMl, syringe]); // eslint-disable-line
  const unitsPerMl   = syringe === 'U100' ? 100 : 40;
  const mlPerUnit    = 1 / unitsPerMl;
  const totalVol     = bacWater;
  const concTotal    = totalVol > 0 ? totalMg / totalVol : 0;
  const totalUnits   = vialMl > 0 ? vialMl * unitsPerMl : 0;
  const numFmt       = (n, d = 1) => isFinite(n) && n > 0 ? n.toFixed(d) : '—';

  function updateComp(i, val) {
    setComponents(prev => prev.map((c, idx) => idx === i ? { ...c, mg: parseFloat(val) || 0 } : c));
  }

  return (
    <div style={{ background: '#0a0d15', border: `1px solid ${def.color}22`, borderLeft: `3px solid ${def.color}`, borderRadius: 6, padding: '1.1rem 1.25rem', marginBottom: '1rem' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.85rem' }}>
        <div>
          <span style={{ fontSize: '0.78rem', color: def.color, letterSpacing: '0.04em' }}>{def.label}</span>
          {def.unitNote && <span style={{ fontSize: '0.65rem', fontFamily: 'monospace', color: '#445', marginLeft: '0.75rem' }}>{def.unitNote}</span>}
        </div>
        <button onClick={() => setShowVars(p => !p)} style={{ ...S.btn(def.color, '#0a0d15', def.color + '44'), fontSize: '0.65rem' }}>
          {showVars ? '▲ Hide' : '▼ Variables'}
        </button>
      </div>

      {/* Warning banner */}
      {def.warning && (
        <div style={{ background: '#1a0828', border: '1px solid #4a1060', borderRadius: 4, padding: '0.5rem 0.75rem', marginBottom: '0.85rem', fontSize: '0.7rem', fontFamily: 'monospace', color: '#8a4090', lineHeight: 1.6 }}>
          ⚠ NOT FDA APPROVED · Research reference only · Do not use compounded retatrutide
        </div>
      )}

      {/* Variable inputs */}
      {showVars && (
        <div style={{ background: '#080b10', border: '1px solid #1a1d28', borderRadius: 5, padding: '0.9rem', marginBottom: '0.85rem' }}>
          {components.length > 1 && (
            <>
              <div style={{ fontSize: '0.63rem', fontFamily: 'monospace', color: '#445', letterSpacing: '0.1em', marginBottom: '0.6rem' }}>COMPOSITION (mg)</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '0.5rem', marginBottom: '0.85rem' }}>
                {components.map((c, i) => (
                  <div key={c.label}>
                    <div style={S.label}>{c.label}</div>
                    <input type="number" step="1" min="0" value={c.mg}
                      onChange={e => updateComp(i, e.target.value)}
                      style={{ ...S.input, width: '100%' }} />
                  </div>
                ))}
              </div>
            </>
          )}
          <div style={{ fontSize: '0.63rem', fontFamily: 'monospace', color: '#445', letterSpacing: '0.1em', marginBottom: '0.6rem' }}>RECONSTITUTION</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '0.5rem' }}>
            <div>
              <div style={S.label}>Peptide (mg in vial)</div>
              <input type="number" step="1" min="1" value={totalMg} readOnly={components.length > 1}
                onChange={e => components.length === 1 && setComponents([{ ...components[0], mg: parseFloat(e.target.value) || 0 }])}
                style={{ ...S.input, width: '100%', opacity: components.length > 1 ? 0.5 : 1 }} />
            </div>
            <div>
              <div style={S.label}>BAC Water (mL)</div>
              <input type="number" step="0.5" min="0.5" max="20" value={bacWater}
                onChange={e => setBacWater(parseFloat(e.target.value) || def.bacWater)}
                style={{ ...S.input, width: '100%' }} />
            </div>
            <div>
              <div style={S.label}>Vial / Autopen (mL)</div>
              <input type="number" step="0.5" min="0.5" max="20" value={vialMl}
                onChange={e => setVialMl(parseFloat(e.target.value) || def.vialMl)}
                style={{ ...S.input, width: '100%' }} />
            </div>
            <div>
              <div style={S.label}>Syringe</div>
              <select value={syringe} onChange={e => setSyringe(e.target.value)} style={{ ...S.input, width: '100%' }}>
                <option value="U100">U-100 (100u/mL)</option>
                <option value="U40">U-40 (40u/mL)</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Summary stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '0.4rem', marginBottom: '0.85rem' }}>
        {[
          { label: 'Concentration', val: `${numFmt(concTotal, 2)} mg/mL` },
          { label: 'BAC Water', val: `${bacWater} mL` },
          { label: 'Vial Volume', val: `${vialMl} mL` },
          { label: `Per Unit (${syringe})`, val: `${numFmt(concTotal * mlPerUnit * 1000, 1)}μg` },
          { label: 'Total Units', val: `~${Math.round(totalUnits)}u` },
          ...(components.length > 1 ? components.map(c => ({
            label: `${c.label}/unit`,
            val: `${numFmt((c.mg / totalVol) * mlPerUnit * 1000, 1)}μg`,
          })) : []),
        ].map(({ label, val }) => (
          <div key={label} style={{ background: '#0c0f1a', border: '1px solid #13161f', borderRadius: 4, padding: '0.45rem 0.7rem' }}>
            <div style={{ fontSize: '0.6rem', fontFamily: 'monospace', color: '#445', marginBottom: '0.15rem' }}>{label}</div>
            <div style={{ fontSize: '0.8rem', fontFamily: 'monospace', color: def.color }}>{val}</div>
          </div>
        ))}
      </div>

      {/* Dose table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.7rem', fontFamily: 'monospace' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #1e2230' }}>
              <th style={{ textAlign: 'left', padding: '4px 8px', color: '#445', fontWeight: 400 }}>Units</th>
              <th style={{ textAlign: 'left', padding: '4px 8px', color: '#445', fontWeight: 400 }}>Vol (mL)</th>
              {components.map(c => (
                <th key={c.label} style={{ textAlign: 'left', padding: '4px 8px', color: '#445', fontWeight: 400 }}>{c.label} μg</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {def.doseRows.map(u => {
              const vol     = (u * mlPerUnit).toFixed(3);
              const inRange = u >= def.rangeMin && u <= def.rangeMax;
              return (
                <tr key={u} style={{ borderBottom: '1px solid #0f1218', background: inRange ? '#0c1018' : 'transparent' }}>
                  <td style={{ padding: '4px 8px', color: inRange ? def.color : '#2a2520' }}>{u}u</td>
                  <td style={{ padding: '4px 8px', color: inRange ? '#888' : '#2a2520' }}>{vol}</td>
                  {components.map(c => (
                    <td key={c.label} style={{ padding: '4px 8px', color: inRange ? '#c8c0b0' : '#2a2520' }}>
                      {numFmt((c.mg / totalVol) * mlPerUnit * u * 1000, 0)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: '0.6rem', fontSize: '0.65rem', color: '#334', fontFamily: 'monospace', lineHeight: 1.75 }}>
        <span style={{ color: '#2a2a18' }}>■</span> highlighted = protocol range ({def.rangeMin}–{def.rangeMax}u) · {def.notes}
      </div>
    </div>
  );
}

// Keep KlowCalculator as alias for backward compat in KLOW tab
function KlowCalculator() {
  // Vial variables
  const [vialMg, setVialMg]     = useState(80);
  const [ghkMg, setGhkMg]       = useState(50);
  const [bpcMg, setBpcMg]       = useState(10);
  const [tb500Mg, setTb500Mg]   = useState(10);
  const [kpvMg, setKpvMg]       = useState(10);
  // Reconstitution variables
  const [bacWater, setBacWater]  = useState(3.0);
  const [vialMl, setVialMl]     = useState(3.0);   // autopen vial volume
  const [syringeType, setSyringeType] = useState('U100'); // U100 or U40
  const [showVars, setShowVars]  = useState(false);

  // Derived: actual peptide per mL after reconstitution
  // vialMl = total liquid volume in vial after adding bacWater
  // If using autopen with pre-filled vial: bacWater added to existing vialMl of solution
  // Here we treat vialMl as the FINAL total volume (bac water fills to this)
  const totalVolume  = bacWater;  // total reconstituted volume = bac water added
  const concTotal    = totalVolume > 0 ? vialMg / totalVolume : 0;  // mg/mL
  const concGhk      = totalVolume > 0 ? ghkMg / totalVolume : 0;
  const concBpc      = totalVolume > 0 ? bpcMg / totalVolume : 0;
  const concTb500    = totalVolume > 0 ? tb500Mg / totalVolume : 0;
  const concKpv      = totalVolume > 0 ? kpvMg / totalVolume : 0;

  // Units per mL depends on syringe type
  const unitsPerMl   = syringeType === 'U100' ? 100 : 40;
  const mlPerUnit    = 1 / unitsPerMl;

  // μg per unit
  const ugTotalPerUnit = (concTotal * mlPerUnit * 1000);
  const ugGhkPerUnit   = (concGhk   * mlPerUnit * 1000);
  const ugBpcPerUnit   = (concBpc   * mlPerUnit * 1000);
  const ugTb500PerUnit = (concTb500 * mlPerUnit * 1000);
  const ugKpvPerUnit   = (concKpv   * mlPerUnit * 1000);

  // Autopen: vialMl is the vial capacity — doses available
  const totalDoses = vialMl > 0 ? (vialMl / mlPerUnit) : 0;

  const doseRows = [5, 10, 15, 20, 25, 30, 40, 45, 50];

  const numFmt = (n, dec=0) => isFinite(n) ? n.toFixed(dec) : '—';

  return (
    <div style={{ background: '#0a0d15', border: '1px solid #1a2030', borderRadius: 6, padding: '1.1rem 1.25rem' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div style={{ fontSize: '0.72rem', fontFamily: 'monospace', color: '#e8a840', letterSpacing: '0.08em' }}>
          KLOW RECONSTITUTION CALCULATOR
        </div>
        <button onClick={() => setShowVars(p => !p)} style={{ ...S.btn('#e8a840', '#100c00', '#403010'), fontSize: '0.68rem' }}>
          {showVars ? '▲ Hide Variables' : '▼ Edit Variables'}
        </button>
      </div>

      {/* Variable inputs */}
      {showVars && (
        <div style={{ background: '#080b10', border: '1px solid #1a1d28', borderRadius: 5, padding: '1rem', marginBottom: '1rem' }}>
          <div style={{ fontSize: '0.65rem', fontFamily: 'monospace', color: '#445', letterSpacing: '0.1em', marginBottom: '0.75rem' }}>VIAL COMPOSITION (mg)</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: '0.6rem', marginBottom: '1rem' }}>
            {[
              { label: 'Total Peptide', val: vialMg,   set: setVialMg },
              { label: 'GHK-Cu',        val: ghkMg,    set: setGhkMg },
              { label: 'BPC-157',       val: bpcMg,    set: setBpcMg },
              { label: 'TB-500',        val: tb500Mg,  set: setTb500Mg },
              { label: 'KPV',           val: kpvMg,    set: setKpvMg },
            ].map(({ label, val, set }) => (
              <div key={label}>
                <div style={S.label}>{label}</div>
                <input type="number" step="1" min="0" value={val}
                  onChange={e => set(parseFloat(e.target.value) || 0)}
                  style={{ ...S.input, width: '100%' }} />
              </div>
            ))}
          </div>
          <div style={{ fontSize: '0.65rem', fontFamily: 'monospace', color: '#445', letterSpacing: '0.1em', marginBottom: '0.75rem' }}>RECONSTITUTION</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '0.6rem' }}>
            <div>
              <div style={S.label}>BAC Water (mL)</div>
              <input type="number" step="0.5" min="0.5" max="10" value={bacWater}
                onChange={e => setBacWater(parseFloat(e.target.value) || 3)}
                style={{ ...S.input, width: '100%' }} />
            </div>
            <div>
              <div style={S.label}>Vial Volume / Autopen (mL)</div>
              <input type="number" step="0.5" min="0.5" max="10" value={vialMl}
                onChange={e => setVialMl(parseFloat(e.target.value) || 3)}
                style={{ ...S.input, width: '100%' }} />
            </div>
            <div>
              <div style={S.label}>Syringe Type</div>
              <select value={syringeType} onChange={e => setSyringeType(e.target.value)} style={{ ...S.input, width: '100%' }}>
                <option value="U100">U-100 (100u/mL)</option>
                <option value="U40">U-40 (40u/mL)</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Summary stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '0.5rem', marginBottom: '1rem' }}>
        {[
          { label: 'Concentration', val: `${numFmt(concTotal, 2)} mg/mL` },
          { label: 'BAC Water Added', val: `${bacWater} mL` },
          { label: 'Autopen Volume', val: `${vialMl} mL` },
          { label: `Per Unit (${syringeType})`, val: `${numFmt(ugTotalPerUnit, 1)}μg total` },
          { label: 'GHK-Cu / unit', val: `${numFmt(ugGhkPerUnit, 1)}μg` },
          { label: 'Total Units in Vial', val: `~${numFmt(totalDoses, 0)}u` },
        ].map(({ label, val }) => (
          <div key={label} style={{ background: '#0c0f1a', border: '1px solid #13161f', borderRadius: 4, padding: '0.5rem 0.75rem' }}>
            <div style={{ fontSize: '0.62rem', fontFamily: 'monospace', color: '#445', marginBottom: '0.2rem' }}>{label}</div>
            <div style={{ fontSize: '0.82rem', fontFamily: 'monospace', color: '#e8a840' }}>{val}</div>
          </div>
        ))}
      </div>

      {/* Dose table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.71rem', fontFamily: 'monospace' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #1e2230' }}>
              {['Units', 'Vol (mL)', 'Total μg', 'GHK-Cu μg', 'BPC-157 μg', 'TB-500 μg', 'KPV μg'].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '5px 8px', color: '#445', fontWeight: 400 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {doseRows.map(u => {
              const vol     = (u * mlPerUnit).toFixed(3);
              const total   = (u * ugTotalPerUnit).toFixed(0);
              const ghk     = (u * ugGhkPerUnit).toFixed(0);
              const bpc     = (u * ugBpcPerUnit).toFixed(0);
              const tb      = (u * ugTb500PerUnit).toFixed(0);
              const kpv     = (u * ugKpvPerUnit).toFixed(0);
              const inRange = u >= 10 && u <= 45;
              return (
                <tr key={u} style={{ borderBottom: '1px solid #0f1218', background: inRange ? '#0c1018' : 'transparent' }}>
                  <td style={{ padding: '5px 8px', color: inRange ? '#e8a840' : '#3a3530' }}>{u}u</td>
                  <td style={{ padding: '5px 8px', color: inRange ? '#888' : '#2a2520' }}>{vol}</td>
                  <td style={{ padding: '5px 8px', color: inRange ? '#c8c0b0' : '#2a2520' }}>{total}</td>
                  <td style={{ padding: '5px 8px', color: inRange ? '#7ec8a0' : '#2a2520' }}>{ghk}</td>
                  <td style={{ padding: '5px 8px', color: inRange ? '#888' : '#2a2520' }}>{bpc}</td>
                  <td style={{ padding: '5px 8px', color: inRange ? '#888' : '#2a2520' }}>{tb}</td>
                  <td style={{ padding: '5px 8px', color: inRange ? '#888' : '#2a2520' }}>{kpv}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Protocol range note */}
      <div style={{ marginTop: '0.6rem', fontSize: '0.67rem', color: '#334', fontFamily: 'monospace', lineHeight: 1.75 }}>
        <span style={{ color: '#4a3a18' }}>■</span> highlighted rows = protocol range (10–45u) ·
        inject bac water slowly down vial wall · swirl gently, do not shake ·
        use within 7–14 days refrigerated · never refreeze ·
        autopen vial: {vialMl}mL = ~{numFmt(totalDoses, 0)} units total
      </div>
    </div>
  );
}

// ── KLOW Cycle Tracker ───────────────────────────────────────────────────────

function KlowCycleTracker({ cycles, onAdd, onEnd }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ start_date: today(), notes: '' });
  const activeCycle = cycles.find(c => !c.end_date);

  const daysSince = (d) => {
    const diff = Date.now() - new Date(d + 'T00:00:00').getTime();
    return Math.floor(diff / (24 * 60 * 60 * 1000));
  };

  const cycleDay = activeCycle ? daysSince(activeCycle.start_date) + 1 : null;
  const weekDay = activeCycle ? ((daysSince(activeCycle.start_date) % 7) + 1) : null;
  const isOnDay = weekDay ? weekDay <= 5 : false;

  return (
    <div>
      {activeCycle && (
        <div style={{ ...S.card(isOnDay ? '#1a3020' : '#2a1a10', isOnDay ? '#4a8a60' : '#8a5030'), marginBottom: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.5rem' }}>
            <div>
              <div style={{ fontSize: '0.72rem', fontFamily: 'monospace', color: isOnDay ? '#4a8a60' : '#8a5030', letterSpacing: '0.08em', marginBottom: '0.25rem' }}>
                ACTIVE CYCLE · DAY {cycleDay} · {isOnDay ? '✓ INJECT TODAY' : '✗ REST DAY'}
              </div>
              <div style={S.mono('#888')}>Started {fmtDate(activeCycle.start_date)} · 5-on/2-off pattern</div>
              <div style={{ marginTop: '0.5rem', display: 'flex', gap: '4px' }}>
                {[1,2,3,4,5,6,7].map(d => (
                  <div key={d} style={{
                    width: 28, height: 28, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.65rem', fontFamily: 'monospace',
                    background: d <= 5 ? '#0a2010' : '#1a0a08',
                    border: `1px solid ${d === weekDay ? (isOnDay ? '#4a8a60' : '#8a5030') : '#13161f'}`,
                    color: d === weekDay ? '#c8c0b0' : d <= 5 ? '#3a6a40' : '#4a2010',
                  }}>
                    {d <= 5 ? '●' : '○'}
                  </div>
                ))}
              </div>
            </div>
            <button onClick={() => onEnd(activeCycle.id)} style={S.btn('#8a5030', '#1a0a08', '#3a1510')}>End Cycle</button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
        <span style={S.mono('#334')}>{cycles.length} CYCLE{cycles.length !== 1 ? 'S' : ''} LOGGED</span>
        {!activeCycle && (
          <button onClick={() => setAdding(!adding)} style={S.btn('#e8a840', '#100c00', '#403010')}>
            {adding ? '✕ Cancel' : '+ Start New Cycle'}
          </button>
        )}
      </div>

      {adding && (
        <div style={{ ...S.card(), marginBottom: '1rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <Field label="Start Date">
            <input type="date" value={form.start_date} onChange={e => setForm(p => ({ ...p, start_date: e.target.value }))} style={{ ...S.input, width: 140 }} />
          </Field>
          <Field label="Notes (optional)">
            <input type="text" placeholder="Protocol version, vial lot..." value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} style={{ ...S.input, width: 220 }} />
          </Field>
          <button onClick={() => { onAdd(form); setAdding(false); setForm({ start_date: today(), notes: '' }); }} style={S.btn('#e8a840', '#100c00', '#403010')}>Start</button>
        </div>
      )}

      {cycles.filter(c => c.end_date).map(c => (
        <div key={c.id} style={{ ...S.card(), marginBottom: '0.4rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <span style={S.mono('#555')}>{fmtDate(c.start_date)} → {fmtDate(c.end_date)}</span>
            <span style={{ ...S.mono('#334'), marginLeft: '0.75rem' }}>{daysSince(c.start_date)} days</span>
          </div>
          {c.notes && <span style={S.mono('#334')}>{c.notes}</span>}
        </div>
      ))}
    </div>
  );
}

// ── Main App ─────────────────────────────────────────────────────────────────

export default function App() {
  const [tab, setTab] = useState('log');
  const [injections, setInjections] = useState([]);
  const [biomarkers, setBiomarkers] = useState([]);
  const [klowCycles, setKlowCycles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLogForm, setShowLogForm] = useState(false);
  const [showBioForm, setShowBioForm] = useState(false);

  // Shared reconstitution settings — updated by ReconCalc, read by Protocol tab
  const [reconSettings, setReconSettings] = useState(
    Object.fromEntries(
      Object.entries(RECON_DEFAULTS).map(([k, d]) => [k, {
        totalMg:  d.components.reduce((s, c) => s + c.mg, 0),
        bacWater: d.bacWater,
        vialMl:   d.vialMl,
        syringe:  d.syringe,
      }])
    )
  );

  function updateReconSettings(key, patch) {
    setReconSettings(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  }

  const [logForm, setLogForm] = useState({
    date: today(), compound: 'klow', dose: '', unit: 'units', route: 'SubQ',
    site: 'Abdomen L-Upper', side_effects: '', notes: ''
  });
  const [bioForm, setBioForm] = useState({ date: today(), marker: 'PSA', value: '', unit: '', notes: '' });

  // Load all data
  const reload = useCallback(async () => {
    try {
      const [inj, bio, cyc] = await Promise.all([
        api.get('/api/injections'),
        api.get('/api/biomarkers'),
        api.get('/api/klow-cycles'),
      ]);
      setInjections(inj);
      setBiomarkers(bio);
      setKlowCycles(cyc);
    } catch (e) {
      console.error('Load error:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // Update unit when compound changes
  useEffect(() => {
    const c = COMPOUNDS[logForm.compound];
    setLogForm(p => ({ ...p, unit: c.unit, dose: c.defaultDose, route: c.routes[0] }));
  }, [logForm.compound]);

  async function addInjection() {
    if (!logForm.dose) return;
    await api.post('/api/injections', logForm);
    setLogForm(p => ({ ...p, dose: COMPOUNDS[p.compound].defaultDose, side_effects: '', notes: '' }));
    setShowLogForm(false);
    reload();
  }

  async function delInjection(id) {
    await api.del(`/api/injections/${id}`);
    setInjections(prev => prev.filter(e => e.id !== id));
  }

  async function addBiomarker() {
    if (!bioForm.value) return;
    await api.post('/api/biomarkers', bioForm);
    setBioForm(p => ({ ...p, value: '', notes: '' }));
    setShowBioForm(false);
    reload();
  }

  async function delBiomarker(id) {
    await api.del(`/api/biomarkers/${id}`);
    setBiomarkers(prev => prev.filter(b => b.id !== id));
  }

  async function addKlowCycle(form) {
    await api.post('/api/klow-cycles', form);
    reload();
  }

  async function endKlowCycle(id) {
    await api.patch(`/api/klow-cycles/${id}`, { end_date: today(), notes: '' });
    reload();
  }

  // Stats
  const firstEntry = [...injections].reverse()[0];
  const weekNum = firstEntry ? weeksSince(firstEntry.date) : 1;
  const currentPhase = weekNum <= 2 ? PHASE_DEFS[0] : weekNum <= 4 ? PHASE_DEFS[1] : PHASE_DEFS[2];
  const counts = Object.keys(COMPOUNDS).reduce((a, k) => ({ ...a, [k]: injections.filter(e => e.compound === k).length }), {});

  const TABS = [
    { id: 'log', label: 'Injection Log' },
    { id: 'biomarkers', label: 'Biomarkers' },
    { id: 'recon', label: 'Reconstitution' },
    { id: 'protocol', label: 'Protocol' },
    { id: 'safety', label: 'Safety' },
  ];

  if (loading) return (
    <div style={{ ...S.page, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.82rem', fontFamily: 'monospace', color: '#334' }}>
      Loading protocol data...
    </div>
  );

  return (
    <div style={S.page}>
      {/* Hidden ReconCalc mounts — always active so reconSettings stays current regardless of tab */}
      <div style={{ display: 'none' }}>
        {Object.keys(RECON_DEFAULTS).map(k => (
          <ReconCalc key={k} compoundKey={k} onSettingsChange={updateReconSettings} />
        ))}
      </div>
      {/* Header */}
      <div style={{ borderBottom: '1px solid #1a1d26', padding: '1.5rem 1.75rem 1.1rem' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.5rem' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#4a9eff', boxShadow: '0 0 8px #4a9eff88' }} />
                <h1 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 400, letterSpacing: '0.06em', color: '#c8c0b0' }}>Research Protocol Log</h1>
              </div>
              <p style={{ margin: '0.3rem 0 0 1.5rem', fontSize: '0.68rem', color: '#334', letterSpacing: '0.08em', fontFamily: 'monospace' }}>
                60YO MALE · POST-PROSTATECTOMY · HTN · TIRZEPATIDE 18MO+
              </p>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <Tag color='#4a9eff' bg='#0d1018' border='#1e2230'>WK {weekNum} · {currentPhase.label.toUpperCase()}</Tag>
              <a href="/api/export" style={{ ...S.btn('#445', '#0c0f1a', '#1e2230'), textDecoration: 'none' }}>↓ Export</a>
            </div>
          </div>
          {/* Compound pills */}
          <div style={{ display: 'flex', gap: '1rem', marginTop: '0.85rem', flexWrap: 'wrap' }}>
            {Object.entries(COMPOUNDS).map(([k, v]) => (
              <div key={k} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: v.color }} />
                <span style={S.mono('#334')}>{v.label}</span>
                <span style={S.mono(v.color)}>{counts[k]}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ borderBottom: '1px solid #13151e', background: '#060810' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', display: 'flex', overflowX: 'auto' }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '0.8rem 1rem', whiteSpace: 'nowrap',
              color: tab === t.id ? '#c8c0b0' : '#334',
              fontSize: '0.72rem', letterSpacing: '0.07em', textTransform: 'uppercase',
              borderBottom: `2px solid ${tab === t.id ? '#4a9eff' : 'transparent'}`,
              fontFamily: 'monospace', transition: 'color 0.15s',
            }}>{t.label}</button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '1.5rem 1.75rem 4rem' }}>

        {/* ── INJECTION LOG ── */}
        {tab === 'log' && (
          <div>
            {/* KLOW 5-on/2-off banner */}
            {(() => {
              const klowEntries = injections.filter(e => e.compound === 'klow').sort((a, b) => new Date(b.date) - new Date(a.date));
              const activeCycle = klowCycles.find(c => !c.end_date);
              if (!activeCycle) return null;
              const startDate = new Date(activeCycle.start_date + 'T00:00:00');
              const daysSince = Math.floor((Date.now() - startDate.getTime()) / (24 * 60 * 60 * 1000));
              const cycleDay  = (daysSince % 7) + 1;
              const isOn      = cycleDay <= 5;
              return (
                <div style={{
                  ...S.card(isOn ? '#1a3020' : '#2a1a10', isOn ? '#4a8a60' : '#8a5030'),
                  marginBottom: '1rem',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem'
                }}>
                  <div>
                    <div style={{ fontSize: '0.7rem', fontFamily: 'monospace', color: isOn ? '#4a8a60' : '#8a5030', letterSpacing: '0.08em', marginBottom: '0.2rem' }}>
                      KLOW · DAY {cycleDay} OF WEEK · {isOn ? '✓ INJECT TODAY' : '✗ REST DAY'}
                    </div>
                    <div style={{ fontSize: '0.68rem', fontFamily: 'monospace', color: '#445' }}>
                      Cycle started {fmtDate(activeCycle.start_date)} · Day {daysSince + 1} total · {klowEntries.length} doses logged
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    {[1,2,3,4,5,6,7].map(d => (
                      <div key={d} style={{
                        width: 26, height: 26, borderRadius: 4,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.62rem', fontFamily: 'monospace',
                        background: d <= 5 ? '#0a2010' : '#1a0a08',
                        border: `1px solid ${d === cycleDay ? (isOn ? '#4a8a60' : '#8a5030') : '#13161f'}`,
                        color: d === cycleDay ? '#c8c0b0' : d <= 5 ? '#2a5a30' : '#3a1808',
                        fontWeight: d === cycleDay ? 'bold' : 'normal',
                      }}>
                        {d <= 5 ? '●' : '○'}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
              <span style={S.mono('#334')}>{injections.length} ENTRIES</span>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {(() => {
                  const activeCycle = klowCycles.find(c => !c.end_date);
                  return activeCycle
                    ? <button onClick={() => endKlowCycle(activeCycle.id)} style={S.btn('#8a5030', '#1a0a08', '#3a1510')}>■ End KLOW Cycle</button>
                    : <button onClick={() => addKlowCycle({ start_date: today(), notes: '' })} style={S.btn('#e8a840', '#100c00', '#403010')}>▶ Start KLOW Cycle</button>;
                })()}
                <button onClick={() => setShowLogForm(p => !p)} style={S.btn('#4a9eff', '#0a1428', '#1e3050')}>
                  {showLogForm ? '✕ Cancel' : '+ Log Injection'}
                </button>
              </div>
            </div>

            {showLogForm && (
              <div style={{ ...S.card(), marginBottom: '1.25rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '0.75rem' }}>
                  <Field label="Date">
                    <input type="date" value={logForm.date} onChange={e => setLogForm(p => ({ ...p, date: e.target.value }))} style={S.input} />
                  </Field>
                  <Field label="Compound">
                    <select value={logForm.compound} onChange={e => setLogForm(p => ({ ...p, compound: e.target.value }))} style={S.input}>
                      {Object.entries(COMPOUNDS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                  </Field>
                  <Field label={`Dose (${COMPOUNDS[logForm.compound].unit})`}>
                    <input type="number" value={logForm.dose} onChange={e => setLogForm(p => ({ ...p, dose: e.target.value }))} style={S.input} />
                  </Field>
                  <Field label="Route">
                    <select value={logForm.route} onChange={e => setLogForm(p => ({ ...p, route: e.target.value }))} style={S.input}>
                      {COMPOUNDS[logForm.compound].routes.map(r => <option key={r}>{r}</option>)}
                    </select>
                  </Field>
                  <Field label="Site">
                    <select value={logForm.site} onChange={e => setLogForm(p => ({ ...p, site: e.target.value }))} style={S.input}>
                      {INJECT_SITES.map(s => <option key={s}>{s}</option>)}
                    </select>
                  </Field>
                  <Field label="Side Effects">
                    <input type="text" placeholder="None / describe" value={logForm.side_effects} onChange={e => setLogForm(p => ({ ...p, side_effects: e.target.value }))} style={S.input} />
                  </Field>
                </div>
                <Field label="Notes">
                  <textarea value={logForm.notes} onChange={e => setLogForm(p => ({ ...p, notes: e.target.value }))} rows={2} placeholder="Observations..." style={{ ...S.input, resize: 'vertical', marginTop: '0.75rem' }} />
                </Field>
                <button onClick={addInjection} style={{ ...S.btn('#4a9eff', '#0a2040', '#1e4070'), marginTop: '0.75rem' }}>Save Entry</button>
              </div>
            )}

            {injections.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem 0', color: '#1e2230', fontFamily: 'monospace', fontSize: '0.82rem' }}>No injections logged yet.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {injections.map(e => {
                  const c = COMPOUNDS[e.compound] || { label: e.compound, color: '#555', unit: '' };
                  return (
                    <div key={e.id} style={{ ...S.card('#13161f'), borderLeft: `3px solid ${c.color}`, display: 'grid', gridTemplateColumns: '95px 1fr 1fr 1fr auto', gap: '0.5rem', alignItems: 'center' }}>
                      <span style={S.mono('#334')}>{fmtDate(e.date)}</span>
                      <span style={{ fontSize: '0.82rem', color: c.color }}>{c.label}</span>
                      <span style={S.mono()}>{e.dose}{c.unit} · {e.route}</span>
                      <span style={S.mono('#445')}>{e.site}</span>
                      <button onClick={() => delInjection(e.id)} style={{ background: 'none', border: 'none', color: '#1e2230', cursor: 'pointer', fontSize: '0.8rem' }}>✕</button>
                      {(e.notes || e.side_effects) && (
                        <div style={{ gridColumn: '1 / -1', fontSize: '0.7rem', color: '#334', fontFamily: 'monospace', borderTop: '1px solid #0f1218', paddingTop: '0.4rem', marginTop: '0.1rem' }}>
                          {e.side_effects && <span style={{ color: '#8a5030' }}>SE: {e.side_effects} · </span>}
                          {e.notes}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── RECONSTITUTION ── */}
        {tab === 'recon' && (
          <div>
            <div style={{ fontSize: '0.68rem', fontFamily: 'monospace', color: '#334', lineHeight: 1.8, marginBottom: '1.25rem' }}>
              Defaults: 3mL BAC water · 3mL autopen vial · U-100 syringe. Use <strong style={{ color: '#555' }}>▼ Variables</strong> on each card to adjust peptide mg, reconstitution volume, vial size, or syringe type. Highlighted rows = protocol dose range. Changes sync to Protocol tab units.
            </div>
            {Object.keys(RECON_DEFAULTS).map(k => (
              <ReconCalc key={k} compoundKey={k} onSettingsChange={updateReconSettings} />
            ))}
          </div>
        )}

        {/* ── BIOMARKERS ── */}
        {tab === 'biomarkers' && (
          <div>
            <div style={{ background: '#1a0a08', border: '1px solid #3a1510', borderRadius: 5, padding: '0.75rem 1rem', marginBottom: '1rem', display: 'flex', gap: '0.75rem' }}>
              <span style={{ color: '#c04030' }}>⚠</span>
              <div>
                <div style={{ fontSize: '0.68rem', fontFamily: 'monospace', color: '#c04030', letterSpacing: '0.08em', marginBottom: '0.2rem' }}>PRIMARY SAFETY GATE — PSA</div>
                <div style={{ fontSize: '0.75rem', color: '#7a4030', lineHeight: 1.6 }}>Any unexplained PSA rise → pause all peptides immediately → consult urologist before resuming.</div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <span style={S.mono('#334')}>{biomarkers.length} READINGS</span>
              <button onClick={() => setShowBioForm(p => !p)} style={S.btn('#9a70f0', '#0e0a18', '#2a1e40')}>
                {showBioForm ? '✕ Cancel' : '+ Add Reading'}
              </button>
            </div>

            {showBioForm && (
              <div style={{ ...S.card(), marginBottom: '1.25rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '0.75rem' }}>
                  <Field label="Date"><input type="date" value={bioForm.date} onChange={e => setBioForm(p => ({ ...p, date: e.target.value }))} style={S.input} /></Field>
                  <Field label="Marker">
                    <select value={bioForm.marker} onChange={e => setBioForm(p => ({ ...p, marker: e.target.value }))} style={S.input}>
                      {BIOMARKERS.map(m => <option key={m}>{m}</option>)}
                    </select>
                  </Field>
                  <Field label="Value"><input type="text" placeholder="0.8" value={bioForm.value} onChange={e => setBioForm(p => ({ ...p, value: e.target.value }))} style={S.input} /></Field>
                  <Field label="Unit"><input type="text" placeholder="ng/mL" value={bioForm.unit} onChange={e => setBioForm(p => ({ ...p, unit: e.target.value }))} style={S.input} /></Field>
                  <Field label="Notes"><input type="text" placeholder="Lab / context" value={bioForm.notes} onChange={e => setBioForm(p => ({ ...p, notes: e.target.value }))} style={S.input} /></Field>
                </div>
                <button onClick={addBiomarker} style={{ ...S.btn('#9a70f0', '#140e28', '#2a1e50'), marginTop: '0.75rem' }}>Save Reading</button>
              </div>
            )}

            {BIOMARKERS.filter(m => biomarkers.some(b => b.marker === m)).map(marker => {
              const readings = biomarkers.filter(b => b.marker === marker).sort((a, b) => new Date(b.date) - new Date(a.date));
              const isPSA = marker === 'PSA';
              return (
                <div key={marker} style={{ marginBottom: '1.25rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem' }}>
                    {isPSA && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#c04030' }} />}
                    <span style={{ fontSize: '0.68rem', fontFamily: 'monospace', color: isPSA ? '#c04030' : '#4a7a9a', letterSpacing: '0.1em', textTransform: 'uppercase' }}>{marker}</span>
                    <span style={S.mono('#334')}>({readings.length})</span>
                  </div>
                  {readings.map(r => (
                    <div key={r.id} style={{ ...S.card(isPSA ? '#2a1510' : '#13161f'), marginBottom: '0.35rem', display: 'grid', gridTemplateColumns: '95px 90px 1fr auto', gap: '0.5rem', alignItems: 'center' }}>
                      <span style={S.mono('#334')}>{fmtDate(r.date)}</span>
                      <span style={{ fontSize: '0.9rem', color: isPSA ? '#c04030' : '#c8c0b0', fontFamily: 'monospace' }}>
                        {r.value}<span style={{ fontSize: '0.65rem', color: '#445', marginLeft: 3 }}>{r.unit}</span>
                      </span>
                      <span style={S.mono('#334')}>{r.notes}</span>
                      <button onClick={() => delBiomarker(r.id)} style={{ background: 'none', border: 'none', color: '#1e2230', cursor: 'pointer' }}>✕</button>
                    </div>
                  ))}
                </div>
              );
            })}
            {biomarkers.length === 0 && <div style={{ textAlign: 'center', padding: '3rem 0', color: '#1e2230', fontFamily: 'monospace' }}>No readings logged yet.</div>}
          </div>
        )}

        {/* ── PROTOCOL ── */}
        {tab === 'protocol' && (
          <div>
            <SectionHeader>Phase Tracker — Week {weekNum}</SectionHeader>
            <div style={{ marginBottom: '0.4rem', fontSize: '0.65rem', fontFamily: 'monospace', color: '#334', textAlign: 'right' }}>
              units derived from Reconstitution tab settings
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem', marginBottom: '0.5rem' }}>
              {PHASE_DEFS.map((p, i) => {
                const active = currentPhase.label === p.label;
                return (
                  <div key={i} style={{ background: active ? '#0a1428' : '#0c0f1a', border: `1px solid ${active ? '#1e3a60' : '#13161f'}`, borderRadius: 6, padding: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                      <span style={S.mono(active ? '#4a9eff' : '#334')}>Wk {p.week}</span>
                      <Tag color={active ? '#4a9eff' : '#334'} bg={active ? '#0d2040' : '#111318'} border={active ? '#1e3a60' : '#1a1d26'}>{p.label}</Tag>
                    </div>
                    {p.compounds.map(c => (
                      <div key={c.key} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem', gap: '0.25rem' }}>
                        <span style={{ ...S.mono(c.color), opacity: active ? 1 : 0.35, flexShrink: 0 }}>{c.label}</span>
                        <span style={{ ...S.mono(active ? '#777' : '#222'), textAlign: 'right', fontSize: '0.68rem' }}>
                          {getPhaseDoseLabel(c, reconSettings)}
                        </span>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>

            <SectionHeader>Compound Details</SectionHeader>
            {[
              { name: 'Tirzepatide', color: '#4a9eff', rows: [
                ['Dose', '5–10mg weekly'],
                ['Route', 'SubQ — abdomen / thigh / posterior arm, rotate'],
                ['Goal', 'Neuroinflammation reduction · insulin signaling · amyloid clearance · CVD protection'],
                ['Duration', 'Continuous — 18mo exposure is accumulating benefit. Do not discontinue.'],
                ['Step-down', 'Consider 5mg/wk maintenance once at weight plateau for longevity protocol'],
              ]},
              { name: 'Semaglutide (Ozempic / Wegovy)', color: '#a06af0', rows: [
                ['Class', 'GLP-1 receptor agonist (single agonist) · predecessor to tirzepatide in this protocol'],
                ['Doses', 'Ozempic: 0.25 / 0.5 / 1.0 / 2.0mg weekly SubQ · Wegovy: up to 2.4mg weekly SubQ'],
                ['Route', 'SubQ — abdomen / thigh / posterior arm, rotate weekly'],
                ['Mechanism', 'GLP-1 receptor agonism · appetite suppression · insulin sensitization · neuroinflammation reduction'],
                ['Neuro note', 'EVOKE Phase 3 (Nov 2025): oral semaglutide did not slow established Alzheimer\'s progression. Observational data (40–70% dementia risk reduction) remains valid for prevention in pre-symptomatic subjects.'],
                ['vs Tirz', 'Tirzepatide adds GIP agonism — broader metabolic effect, greater weight loss (~21% vs ~15%), stronger cardiometabolic profile. Semaglutide remains valid if tirzepatide is not tolerated.'],
                ['Re-entry', 'If switching back from tirzepatide: restart at 0.25mg × 4wk titration regardless of prior dose history.'],
                ['Storage', 'Refrigerate 2–8°C · do not freeze · use within 56 days of first use (pen) · protect from light'],
              ]},
              { name: 'KLOW (replaces standalone GHK-Cu)', color: '#e8a840', rows: [
                ['Vial', '80mg total: GHK-Cu 50mg + BPC-157 10mg + TB-500 10mg + KPV 10mg'],
                ['Schedule', '5-on / 2-off · once daily SubQ · evening preferred'],
                ['Titration', 'Wk 1–2: 10–15u · Wk 3–4: 20–30u · Wk 5+: 30–45u'],
                ['Cycle', '8–12 weeks on / 4 weeks off'],
                ['BPC-157', 'Gut health, tendon/muscle repair, angiogenesis. No IGF-1 stimulation.'],
                ['TB-500', 'Cell migration, endothelial regeneration, anti-inflammatory. No androgenic activity.'],
                ['KPV', 'NF-κB + MAPK inhibition. Potent anti-inflammatory. No melanotropic effects.'],
              ]},
              { name: 'NAD+', color: '#7ec8a0', rows: [
                ['Loading', 'Wk 1–2: 25mg every other day (tolerance phase)'],
                ['Ramp', 'Wk 3–4: 50mg 3×/week'],
                ['Maintenance', '100mg SubQ 2–3×/week or IM 1–2×/week · max 300mg/week'],
                ['Quarterly', 'IV reset 500–1000mg via clinic'],
                ['Timing', 'Morning — NAMPT circadian peak'],
                ['Validate', 'Jinfiniti intracellular NAD+ test at Week 6 (target: 40–100μM)'],
                ['Adjunct', 'Apigenin 50mg or quercetin 500mg daily as CD38 inhibitor'],
              ]},
              { name: 'Retatrutide (future)', color: '#c070e0', rows: [
                ['Status', 'Not FDA approved. Earliest 2027. Phase 3 TRIUMPH trials ongoing.'],
                ['Mechanism', 'Triple agonist: GLP-1 + GIP + Glucagon receptors'],
                ['Plan', 'Evaluate replacing tirzepatide post-approval. Review neurological safety data first.'],
                ['Watch', 'Eli Lilly TRIUMPH readouts 2026 · Dysesthesia signal (20% at 12mg) needs monitoring'],
              ]},
            ].map(({ name, color, rows }) => (
              <div key={name} style={{ ...S.card('#13161f', color), marginBottom: '0.75rem' }}>
                <div style={{ fontSize: '0.8rem', color, marginBottom: '0.7rem' }}>{name}</div>
                {rows.map(([lbl, val]) => (
                  <div key={lbl} style={{ display: 'flex', gap: '1rem', marginBottom: '0.3rem' }}>
                    <span style={{ ...S.mono('#445'), width: 90, flexShrink: 0 }}>{lbl}</span>
                    <span style={{ fontSize: '0.73rem', color: '#6a6560', lineHeight: 1.55 }}>{val}</span>
                  </div>
                ))}
              </div>
            ))}

            <SectionHeader>Assessment Schedule</SectionHeader>
            {[
              { tp: 'Baseline', markers: 'PSA, CBC, CMP, hsCRP, IGF-1, Lipids, BP, Weight', note: 'MoCA cognitive baseline · Jinfiniti NAD+ baseline if possible' },
              { tp: 'Week 6', markers: 'PSA, hsCRP, BP, Weight', note: 'Jinfiniti intracellular NAD+ test · KLOW dose assessment' },
              { tp: 'Month 3', markers: 'Full panel: PSA, CBC, CMP, hsCRP, IGF-1, Lipids, BP, Weight', note: 'PSA trend · tirzepatide dose eval · KLOW cycle review' },
              { tp: 'Month 6', markers: 'Full panel', note: 'NAD+ frequency optimization · MoCA repeat · consider IV NAD+ reset' },
              { tp: 'Annually', markers: 'Full panel + MoCA', note: 'Long-term trajectory · consider amyloid/tau biomarkers if accessible' },
            ].map(({ tp, markers, note }) => (
              <div key={tp} style={{ ...S.card(), marginBottom: '0.5rem', display: 'grid', gridTemplateColumns: '90px 1fr', gap: '0.75rem' }}>
                <span style={S.mono('#4a9eff')}>{tp}</span>
                <div>
                  <div style={{ fontSize: '0.72rem', color: '#6a6560', marginBottom: '0.2rem' }}>{markers}</div>
                  <div style={{ fontSize: '0.68rem', fontFamily: 'monospace', color: '#3a5a40' }}>→ {note}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── SAFETY ── */}
        {tab === 'safety' && (
          <div>
            <div style={{ background: '#1a0a08', border: '1px solid #4a1510', borderRadius: 6, padding: '1rem 1.25rem', marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.68rem', fontFamily: 'monospace', color: '#c04030', letterSpacing: '0.1em', marginBottom: '0.4rem' }}>⚠ PRIMARY SAFETY GATE — PSA</div>
              <p style={{ margin: 0, fontSize: '0.78rem', color: '#8a4030', lineHeight: 1.7 }}>
                Post-prostatectomy history requires PSA monitoring every 3 months minimum. Any unexplained rise above baseline — regardless of absolute value — is grounds to immediately pause all peptide compounds and consult urology. This gate supersedes all other protocol considerations without exception.
              </p>
            </div>

            <SectionHeader>Decision Gates</SectionHeader>
            {SAFETY_GATES.map((g, i) => (
              <div key={i} style={{ ...S.card(
                g.level === 'critical' ? '#3a1510' : g.level === 'warning' ? '#3a2a10' : '#1a2a1a',
                g.level === 'critical' ? '#c04030' : g.level === 'warning' ? '#c08030' : '#4a8a60'
              ), marginBottom: '0.6rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
                  <div>
                    <div style={{ fontSize: '0.78rem', color: g.level === 'critical' ? '#c04030' : g.level === 'warning' ? '#c08030' : '#4a8a60', marginBottom: '0.35rem' }}>{g.marker}</div>
                    <div style={{ fontSize: '0.73rem', color: '#5a5550', lineHeight: 1.6 }}>→ {g.action}</div>
                  </div>
                  <Tag color='#334'>{g.level.toUpperCase()}</Tag>
                </div>
              </div>
            ))}

            <SectionHeader>Interaction Notes</SectionHeader>
            {[
              { t: 'Tirzepatide + KLOW', n: 'No known interactions. Tirzepatide = systemic metabolic/neuroinflammatory signal. KLOW = local tissue remodeling + NF-κB inhibition. Mechanistically additive and non-overlapping.' },
              { t: 'Tirzepatide + NAD+', n: 'No known interactions. Both address mitochondrial dysfunction and neuroinflammation via complementary pathways. NAD+ supports the cellular machinery tirzepatide signals.' },
              { t: 'KLOW + NAD+', n: 'Theoretical synergy: NAD+ (sirtuin activation) may amplify GHK-Cu gene expression effects. KPV NF-κB inhibition complements tirzepatide anti-inflammatory signaling. Run both concurrently.' },
              { t: 'KLOW + standalone GHK-Cu', n: 'Do NOT run concurrently. KLOW already contains 50mg GHK-Cu per vial (dominant component). Stacking would double GHK-Cu dose without clear benefit.' },
              { t: 'Injection Site Conflicts', n: 'Do not inject KLOW and NAD+ at the same site same day. Tirzepatide has its own rotation independent of peptide sites. Log all sites to maintain rotation discipline.' },
              { t: 'GLP-1 + Prostate History', n: 'No androgenic or IGF-1 axis stimulation from any compound in this stack. GHK-Cu (via KLOW) has tumor-suppressive gene expression profile per Pickart research. PSA gate is the primary monitor.' },
              { t: 'NAD+ + Hypertension Meds', n: 'No meaningful interactions documented with common antihypertensives. NAD+ supports endothelial function. Monitor BP as part of standard biomarker tracking.' },
            ].map(({ t, n }) => (
              <div key={t} style={{ ...S.card(), marginBottom: '0.5rem' }}>
                <div style={{ fontSize: '0.75rem', color: '#9a9080', marginBottom: '0.35rem' }}>{t}</div>
                <div style={{ fontSize: '0.72rem', color: '#4a4540', lineHeight: 1.65 }}>{n}</div>
              </div>
            ))}

            <div style={{ marginTop: '1rem', borderTop: '1px solid #13161f', paddingTop: '1rem', fontSize: '0.7rem', color: '#223', fontStyle: 'italic', lineHeight: 1.8 }}>
              This log is for research documentation purposes. All protocols should be co-managed with a urologist or integrative medicine physician familiar with post-prostatectomy monitoring. PSA is the non-negotiable safety checkpoint throughout.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
